import {
  TGroupResourceWithCompanyName,
  TGroupResourceWithFullName,
  TGroupResourceWithName,
  TResourceGroupListItem,
} from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group-form.types';
import { TGroupResourceItem } from '@/shared/features/groups/context/groups-config-context.types';
import { ResourceGroupFragment } from '@/shared/types/types.generated';
import { getFullname } from '@/shared/utils/format';

export const isResourceGroup = (
  resource: TGroupResourceItem,
): resource is TGroupResourceItem & ResourceGroupFragment =>
  resource.__typename === 'ResourceGroupResponseType';

export const hasFullName = (
  resource: TGroupResourceItem,
): resource is TGroupResourceWithFullName =>
  'firstName' in resource || 'lastName' in resource;

export const hasName = (
  resource: TGroupResourceItem,
): resource is TGroupResourceWithName => 'name' in resource;

export const hasCompanyName = (
  resource: TGroupResourceItem,
): resource is TGroupResourceWithCompanyName => 'companyName' in resource;

export const getExcludedResourceGroupIds = (
  resources: TGroupResourceItem[],
  allGroups: TResourceGroupListItem[] | undefined,
): Set<string> => {
  const excluded = new Set(
    resources.filter(isResourceGroup).map((resource) => resource.groupId),
  );

  if (!allGroups) {
    return excluded;
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const group of allGroups) {
      if (
        !excluded.has(group.groupId) &&
        group.parentGroupId &&
        excluded.has(group.parentGroupId)
      ) {
        excluded.add(group.groupId);
        changed = true;
      }
    }
  }

  return excluded;
};

export const getResourceName = (
  resource: TGroupResourceItem,
  unknownLabel: string,
): string => {
  if (hasFullName(resource)) {
    return getFullname({
      firstName: resource.firstName ?? '',
      lastName: resource.lastName ?? '',
    });
  }

  if (hasName(resource) && resource.name) {
    return resource.name;
  }

  if (hasCompanyName(resource) && resource.companyName) {
    return resource.companyName;
  }

  return unknownLabel;
};
